<script setup lang="ts">
import { attachmentIcon } from '../composables/useOrpheusFileAttachment'
import type { ChatMessage } from '../models/orpheus.types'

import { useI18n } from '@/shared/i18n'
import { Button, Icon, Textarea } from '@/shared/ui'

withDefaults(defineProps<{ message: ChatMessage; editing?: boolean; busy?: boolean }>(), {
  editing: false,
  busy: false,
})
const draft = defineModel<string>('draft', { default: '' })
const emit = defineEmits<{ 'cancel-edit': []; 'submit-edit': [] }>()
const { t } = useI18n()

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return
  event.preventDefault()
  emit('submit-edit')
}
</script>

<template>
  <div
    v-if="editing"
    class="user-message__editor grid gap-xs min-w-[min(32rem,_70vw)]"
    @keydown="onKeydown"
  >
    <Textarea v-model="draft" :disabled="busy" auto-grow />
    <div class="user-message__buttons flex justify-end gap-xs">
      <Button size="xs" variant="ghost" :disabled="busy" @click="emit('cancel-edit')">
        {{ t('orpheus.message.cancel') }}
      </Button>
      <Button size="xs" :disabled="busy || !draft.trim()" @click="emit('submit-edit')">
        {{ t('orpheus.message.sendEdit') }}
      </Button>
    </div>
  </div>
  <template v-else>
    <!-- The file the turn carried: name and rows, never the ref (v3 ORP-26203). -->
    <span
      v-if="message.attachment"
      class="mb-xs inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-primary"
      data-testid="orpheus-message-attachment"
    >
      <Icon :name="attachmentIcon(message.attachment.name)" :size="14" weight="light" />
      {{ message.attachment.name }}
      <span v-if="message.attachment.rowCount !== null" class="text-muted">
        {{ t('orpheus.attachment.rows', { count: message.attachment.rowCount }) }}
      </span>
    </span>
    <p class="user-message__content m-0 whitespace-pre-wrap wrap-anywhere">
      {{ message.content }}
    </p>
  </template>
</template>
